import {
	QueryClient,
	QueryCache,
	QueryClientProvider,
	UseQueryOptions,
} from "@tanstack/react-query";
import React, { useMemo } from "react";

import { IEnvContext, useEnvContext } from "./env-context";
import type { FeatureFlags } from "./feature-flags";

declare module "@tanstack/react-query" {
	export interface QueryMeta {
		logToServer: boolean;
		errorPrefix?: string;
	}
}
export interface IReactAppContextValue {
	context: IEnvContext;
	logger: eXpress.ILogger;
	platform: eXpress.platforms.IPlatform;
	client: eXpress.core.IClient;
	editor: eXpress.core.IEditor;
	getFontSizeCorrectorAsync: eXpress.creator.DashboardFontHelper["getFontSizeCorrectorAsync"];
	defaultFontFamily: eXpress.core.FontFamily;
	localizedContent: eXpress.creator.LocalizedContent;
	fontFamilyLoader: eXpress.creator.rendering.FontFamilyLoader;
	clipboard: eXpress.creator.Clipboard;
	googleAnalytics: eXpress.GoogleAnalytics;

	/** Notify when a network request is waiting for a network connection */
	offlineEmitter: eXpress.core.IEventEmitter<boolean>;

	/** Monitor the network connection and connection to the smartphoto servers */
	networkMonitor: eXpress.api.NetworkMonitor;

	featureManager: eXpress.FeatureFlagManager<FeatureFlags>;

	/**
	 * react-query option to hard cache a query result
	 * Didn't use as default to be save by default
	 */

	reactQueryNoReloadOptions: any; // used any, other useQuery looses its types
}

/** Create the value for the React; AppContext */
function createAppContextValue(context: Partial<IEnvContext>): IReactAppContextValue {
	const { core, platform, api, creator } = eXpress;

	const { queryString, ajax, cookies, persistentStorage, fontLoading } = platform;

	eXpress.isDebugMode = queryString.getQueryStringToggle("debug", context.isDebugBuild || false);

	// Store in then global eXpress variable
	const logger = new core.Logger({
		logToConsole: eXpress.isDebugMode,
		platform,
		shouldLogStyled: platform.browserInfo.isChromeBrowser(),
	});

	const defaultFeatures: FeatureFlags = {
		fullScreenFormDialogs: "false",
		transformWithRotate: "false",
		newZoneResize: "false",
		// prefix debug flags with 'debug' so they can be displayed seperately from temporary feature flags in UI?
		debugForceAdvancedEditMode: "false",
		debugAlwaysUpload: "false",
		debugDisableImport: "false",
		debugPreview: "false",
		debugUploadDetails: "false",
		debugUploadChaos: "false",
		debugSelectionBox: "false",
		add2MapZonesPerPage: "false",
		addDefaultMapStyle: "",
		addDefaultMapLocation: "",
		addPOIs: "",
		forceQrTab: "false",
		useDesktopLayout: "false",
	};

	const featureManager = new eXpress.FeatureFlagManager<FeatureFlags>(defaultFeatures, {
		persistentStorage: persistentStorage as eXpress.platforms.PersistentStorage<FeatureFlags>,
		queryString,
	});

	// Creator Context
	if (
		!eXpress.creator.initAppContext(context, {
			logger,
			queryString,
		})
	) {
		throw new Error("Can't initialize app context");
	}

	const services = new api.Services({
		context,
		ajax,
		logger,
	});

	const networkMonitor = new api.NetworkMonitor(
		platform.network,
		() =>
			services.application.ping().then((status) => {
				if (status === api.ServiceStatus.Offline) throw new Error("offline");
			}),
		logger,
	);

	const offlineEmitter = eXpress.core.createEventEmitter<boolean>();
	const networkNotifier = new eXpress.BalancedScope(
		() => {
			offlineEmitter.emit(true);
		},
		() => {
			offlineEmitter.emit(false);
		},
	);

	const client = new api.WebClient({
		context,
		logger,
		logProcess: core.LogProcess.MobileCreator,
		platform,
		serialization: api.serialization,
		services,
		networkMonitor,
		networkNotifier,
	});

	context.account = new eXpress.client.Account({ cookies: platform.cookies });

	const editor = new core.Editor({
		client,
		context,
		getAdvancedEditModePreference: () => {
			if (featureManager.getFeatureFlagAsBoolean("debugForceAdvancedEditMode")) {
				return true;
			}

			return undefined;
		},
		newCommandManager: (editor2) => new eXpress.core.CommandManager(editor2, 20),
	});

	const fontFamilyDict = new core.FontFamilyDictionary({
		client,
		logger,
	});
	const defaultFontFamily = new core.FontFamily(
		fontFamilyDict,
		eXpress.core.text.fallbackFontFamilyName,
		true,
		true,
	);
	const fontFamilyLoader = new creator.rendering.FontFamilyLoader({
		resourceResolver: client,
		ajax,
		fontPlatform: fontLoading,
		logger,
		// To prevent double error logs, we didn't use onLoadException herer because we already missing fonts at rendering
	});

	const clipboard = new creator.Clipboard(editor);

	const dashboardFontHelper = new eXpress.creator.DashboardFontHelper({
		fallbackFontFamily: defaultFontFamily,
		fontFamilyContentLoader: fontFamilyLoader.fontFamilyContentLoader,
		fontFamilyMetricsLoader: fontFamilyLoader.fontFamilyMetricsLoader,
		getMinFontSizeForProductCodeAsync: async (
			fontFamilyName,
			productCode,
			fontFamiliesDictionary,
		) => {
			const result = await client.getFontFamiliesInfoForProduct(
				productCode,
				fontFamiliesDictionary,
			);
			return result.minFontSizes[fontFamilyName.toLowerCase()] ?? 0;
		},
		logger,
		logErrorAsync: client.logErrorAsync.bind(client),
	});

	const localizedContent = new creator.LocalizedContent(client);

	const googleAnalytics = new eXpress.GoogleAnalytics("MobileCreator", {
		editor,
		cookies,
		queryString,
		getAccountId: () => context.account.id,
		arePhotoZonesSupported: () =>
			editor.definition
				? editor.definition.getFillablePhotoZones(false, "photo").length > 0
				: false,
		areTextZonesSupported: () =>
			editor.definition ? editor.definition.getFillableTextZones(false).length > 0 : false,
		areColorBackgroundsSupported: () => !!editor.definition?.product.showBackgroundColorTab,
		areCategoryBackgroundsSupported: () =>
			!!editor.definition?.product.showBackgroundLibraryTab,
		areDesignsSupported: () => !!editor.definition?.product.showDesigns,
		shouldLogGaEvent: (gaEventInfo: eXpress.GaEventInfo) => {
			const gaEventsToExclude = editor.definition?.product?.gaEventsToExclude;
			if (eXpress.utils.isNonEmptyArray(gaEventsToExclude)) {
				if (gaEventsToExclude.includes(gaEventInfo.trackingAction)) {
					return false;
				}
			}
			return true;
		},
	});

	// Build querstring of non default toggles
	const featuresAsArguments = eXpress.utils
		.keys(featureManager.toggles)
		.filter((toggleName) => featureManager.toggles[toggleName] !== defaultFeatures[toggleName])
		.map(
			(toggleName) =>
				`feature.${toggleName}=${encodeURIComponent(
					featureManager.toggles[toggleName] ?? "",
				)}`,
		)
		.join("&");

	if (featuresAsArguments) logger.log(`Enabled features: ${featuresAsArguments}`);

	/** Options for loading the query only once */
	const reactQueryNoReloadOptions: UseQueryOptions = {
		staleTime: Infinity,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	};

	eXpress.getUsedPhotoIds = function getUsedPhotoIds() {
		return editor.assets.photoSelection
			.orderBy(core.Photo.compareByIndexAddedAscending)
			.map((photo) => photo.id);
	};

	eXpress.getUsedPhotoNames = () => {
		return editor.assets.photoSelection.map((photo) => photo.name);
	};

	return {
		context,
		logger,
		platform,
		client,
		editor,
		getFontSizeCorrectorAsync:
			dashboardFontHelper.getFontSizeCorrectorAsync.bind(dashboardFontHelper),
		localizedContent,
		defaultFontFamily,
		fontFamilyLoader,
		googleAnalytics,
		offlineEmitter,
		networkMonitor,
		featureManager,
		reactQueryNoReloadOptions,
		clipboard,
	};
}

// Create AppContext
export const AppContext = React.createContext<IReactAppContextValue>({
	context: {},
} as IReactAppContextValue);
AppContext.displayName = "AppContext";

/**
 * Component that provides a default AppContext and
 * the possibility to adjust default after creation for testing
 */
export function AppContextProvider(props: React.PropsWithChildren) {
	const { children } = props;
	const context = useEnvContext();

	const { isDebugBuild } = context;

	// Create React AppContext value
	const appContextValue = createAppContextValue(context);

	// Make appContext available on console in 'Debug' mode
	// Assuming only one context is used, else global variable overridden
	if (isDebugBuild) {
		(window as any)._appContext = appContextValue;
	}

	// No need to place queryString on appContext, it will be accessable with hook useQueryClient
	const queryClient = useMemo(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						// Log errors by default to the server (can be overriden)
						onError: (err) => {
							appContextValue.logger.error(eXpress.Exception.toJson(err));
							appContextValue.client.logErrorAsync(err, false);
						},
					},
				},
				queryCache: new QueryCache({
					onError: (error, query) => {
						/** To not have errors of already handled error, you need to opt in */
						if (query?.meta?.logToServer) {
							const errorPrefix = query.meta.errorPrefix;

							// Log errors by default to the server (can be overriden)
							if (errorPrefix) appContextValue.logger.error(errorPrefix);
							appContextValue.logger.error(eXpress.Exception.toJson(error));
							appContextValue.client.logErrorAsync(error, false);
						}
					},
				}),
			}),
		[appContextValue.client, appContextValue.logger],
	);

	return (
		<AppContext.Provider value={appContextValue}>
			<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
		</AppContext.Provider>
	);
}
